import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';
import { ChartTooltipCard, ChartTooltipRow, EmptyState, Panel } from './Panel';
import { TimeOfDayPoint } from './metrics';
import { accent, chart, formatCount } from './tokens';

interface BusiestTimesChartProps {
  data: TimeOfDayPoint[];
  rangeLabel: string;
  /** True when the window comes from configured opening hours. */
  usesOperatingHours: boolean;
}

/** End of a half-hour slot, for the tooltip's range label. */
const nextSlot = (slot: string) => {
  const [hours, minutes] = slot.split(':').map(Number);
  const total = (hours * 60 + minutes + 30) % (24 * 60);
  return `${`${Math.floor(total / 60)}`.padStart(2, '0')}:${`${total % 60}`.padStart(2, '0')}`;
};

/**
 * Stamps by time of day across the selected period, in half-hour slots. Answers
 * the staffing question the daily bar chart cannot: when in the day is the
 * queue.
 */
export const BusiestTimesChart: React.FC<BusiestTimesChartProps> = ({
  data,
  rangeLabel,
  usesOperatingHours
}) => {
  const total = data.reduce((sum, point) => sum + point.stamps, 0);
  const peak = data.reduce(
    (best, point) => (point.stamps > best.stamps ? point : best),
    { slot: '', stamps: 0, perDay: 0 }
  );

  const renderTooltip = ({ active, label }: TooltipProps<number, string>) => {
    if (!active) return null;
    const point = data.find((entry) => entry.slot === label);
    if (!point) return null;
    return (
      <ChartTooltipCard title={`${point.slot} – ${nextSlot(point.slot)}`}>
        <ChartTooltipRow
          color={accent.mid}
          label='Stamps'
          value={formatCount(point.stamps)}
        />
        <ChartTooltipRow label='Average per day' value={point.perDay.toFixed(1)} />
      </ChartTooltipCard>
    );
  };

  return (
    <Panel
      title='Busiest times of day'
      subtitle={
        peak.stamps > 0
          ? `Peak at ${peak.slot} · ${rangeLabel}`
          : rangeLabel
      }
      hint={
        usesOperatingHours
          ? 'Stamps grouped into half-hour slots across your opening hours. Scans outside opening hours are folded into the nearest edge slot.'
          : 'Stamps grouped into half-hour slots. Set your opening hours on the Details tab to fix this chart to your trading day.'
      }
    >
      {!total ? (
        <EmptyState
          height={chart.height}
          message='No stamps in this period, so there is no shape to the day yet.'
        />
      ) : (
        <ResponsiveContainer width='100%' height={chart.height}>
          <AreaChart data={data} margin={chart.margin}>
            <defs>
              <linearGradient id='busiest-fill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={accent.brand} stopOpacity={0.3} />
                <stop offset='100%' stopColor={accent.brand} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={chart.gridStroke} />
            <XAxis
              dataKey='slot'
              tick={chart.tick}
              tickLine={false}
              axisLine={{ stroke: chart.gridStroke }}
              minTickGap={26}
            />
            <YAxis
              tick={chart.tick}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={44}
            />
            <Tooltip content={renderTooltip} cursor={{ stroke: accent.mid, strokeWidth: 1 }} />
            <Area
              type='monotone'
              dataKey='stamps'
              stroke={accent.mid}
              strokeWidth={2.25}
              fill='url(#busiest-fill)'
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

export default BusiestTimesChart;
